<script setup>
import { onMounted, ref, computed } from 'vue';
import AdminHeader from '../../AdminHeader.vue';
import PaymentMethodsTable from '@/components/table/Table.vue';
import { getPaymentMethods, togglePaymentMethodActive } from '@/services/paymentMethodsService';
import { useToast } from '@/composables/use-toast';

const { addToast } = useToast();

const isLoading = ref(false);
const paymentMethods = ref([]);
// Search
const searchQuery = ref('');

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(10);

const columns = ref([
    {
        label: 'Logo',
        key: 'icon',
        type: 'image',
    },
    {
        label: 'Name',
        key: 'name',
        type: 'text',
        truncate: 45,
    },
    {
        label: 'Description',
        key: 'description',
        type: 'text',
        truncate: 45,
    },
    {
        label: 'Currency',
        key: 'currency',
        type: 'text',
        truncate: 45,
    },
    {
        label: 'Status',
        key: 'active',
    }
]);

const filteredPaymentMethods = computed(() => {
    if (!searchQuery.value) {
        return paymentMethods.value;
    }
    
    const query = searchQuery.value.toLowerCase();
    return paymentMethods.value.filter(paymentMethod => {
        const nameMatch = paymentMethod.name?.toLowerCase().includes(query);
        const descriptionMatch = paymentMethod.description?.toLowerCase().includes(query);
        const currencyMatch = paymentMethod.currency?.toLowerCase().includes(query);
        
        let createdAtMatch = false;
        let updatedAtMatch = false;
        
        if (paymentMethod.created_at) {
            const createdDate = new Date(paymentMethod.created_at);
            const createdDateStr = createdDate.toLocaleString('en-US').toLowerCase();
            const createdDateIso = paymentMethod.created_at.toLowerCase();
            createdAtMatch = createdDateStr.includes(query) || createdDateIso.includes(query);
        }
        
        if (paymentMethod.updated_at) {
            const updatedDate = new Date(paymentMethod.updated_at);
            const updatedDateStr = updatedDate.toLocaleString('en-US').toLowerCase();
            const updatedDateIso = paymentMethod.updated_at.toLowerCase();
            updatedAtMatch = updatedDateStr.includes(query) || updatedDateIso.includes(query);
        }
        
        return nameMatch || descriptionMatch || currencyMatch || createdAtMatch || updatedAtMatch;
    });
});

const loadPaymentMethodsList = async () => {
    isLoading.value = true;

    try {
        const response = await getPaymentMethods();
        paymentMethods.value = response;
    } catch (error) {
        addToast(error.message, 'error');
    } finally {
        isLoading.value = false;
    }
};

const handleToggleActive = async (paymentMethod) => {
    try {
        const response = await togglePaymentMethodActive(paymentMethod.id, paymentMethod.active);

        if (response) {
            const index = paymentMethods.value.findIndex(pm => pm.id === paymentMethod.id);
            if (index !== -1) {
                paymentMethods.value[index] = { ...paymentMethods.value[index], ...response };
            }
            addToast('Payment Method status updated successfully!', 'success');
        }
    } catch (error) {
        addToast(error.message || 'Error updating payment method status', 'error');
    }
};

onMounted(() => {
    loadPaymentMethodsList();
});
</script>

<template>
    <article class="p-5">
        <header class="bg-echoes-background-black">
            <AdminHeader>
                <template #title>
                    Payment Methods
                </template>
                <template #description>
                    Manage and organize payment methods
                </template>
            </AdminHeader>
        </header>

        <main class="bg-echoes-background-black mb-8">
            <!-- Loading State -->
            <div v-if="isLoading" class="flex justify-center items-center py-8">
                <span class="sr-only">Loading Payment Methods...</span>
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-echoes-primary"></div>
            </div>
            <!-- Data Table -->
            <section v-else aria-label="Payment Methods List">
                <PaymentMethodsTable 
                :data="filteredPaymentMethods" 
                :columns="columns" 
                :empty-message="'No Payment Methods found'" 
                :total-items="filteredPaymentMethods.length"
                :search-query="searchQuery"
                @update:search-query="searchQuery = $event"
                :current-page="currentPage"
                @update:current-page="currentPage = $event"
                :items-per-page="itemsPerPage"
                @update:items-per-page="itemsPerPage = $event"
                >
                    <!-- Status Column -->
                    <template #column-active="{ item }">
                        <div class="flex items-center gap-3">
                            <!-- Toggle Switch -->
                            <button
                                type="button"
                                @click="handleToggleActive(item)"
                                :class="[
                                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
                                    item.active ? 'bg-green-600' : 'bg-gray-600'
                                ]"
                                :title="item.active ? 'Click to deactivate' : 'Click to activate'"
                                :aria-label="`Toggle Payment Method status. Currently ${item.active ? 'active' : 'inactive'}`"
                            >
                                <span
                                    aria-hidden="true"
                                    :class="[
                                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                        item.active ? 'translate-x-5' : 'translate-x-0'
                                    ]"
                                ></span>
                            </button>
                            
                            <!-- Status Label -->
                            <span 
                                :class="[
                                    'text-xs font-medium',
                                    item.active ? 'text-green-400' : 'text-gray-400'
                                ]"
                            >
                                {{ item.active ? 'Active' : 'Inactive' }}
                            </span>
                        </div>
                    </template>

                    <!-- Icon Column -->
                    <template #column-icon="{ item }">
                        <div class="flex items-center gap-2">
                            <img :src="item.icon" alt="Payment Method Icon" class="w-10 h-10 rounded-full">
                        </div>
                    </template>
                </PaymentMethodsTable>
            </section>
        </main>
    </article>
</template>